import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import cx from "classnames";
import _, { isEqual, set } from "lodash";
import {
  Dropdown,
  Button,
  Accordion,
  Container,
  OverlayTrigger,
  Popover,
  Form,
  useAccordionButton,
} from "react-bootstrap";
import { DndContext } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { toast } from "react-toastify";
import { FailureToast, SuccessToast } from "components/Toast";

import { segmentFilterOptions } from "constants/filters";
import {
  cloneAudienceSegment,
  createAudienceSegment,
  deleteAudienceSegment,
  fetchAudienceSegments,
  invalidateSegmnetSelection,
  selectAudienceSegment,
  updateAudienceSegmentOrder,
} from "actions/personalization";
import { actionGroup } from "actions/editor";
import { fetchCustomFormFields } from "actions/formFields";

import InfiniteScroll from "components/InfiniteScroll";
import { SegmentFilter, SegmentContext } from "./segmentFilter";
import { guid } from "services/util";
import { DeletePersonalizationModal } from "../..";
import { LoadingMessage } from "pages/storyboards/storyboardSettings/leadReports/components";

import styles from "./audienceSegment.module.scss";



function CustomToggle({ eventKey, currentSegment, segment }) {
  const dispatch = useDispatch();

  const toggleAccordion = useAccordionButton(eventKey, () => console.log(""));

  const decoratedOnClick = (event) => {
    dispatch(selectAudienceSegment(segment.id));
    event.preventDefault();

    toggleAccordion(event);
  };

  return (
    <div className={styles.detailTag} onClick={decoratedOnClick}>
      <div
        className={cx(styles.actionWrapper, {
          [styles.active]: currentSegment === eventKey,
        })}
      >
        <Button className={cx(styles.expandButton)} variant="info">
          <i className={cx("cleverstory-accordian-drop-down")} />
        </Button>
      </div>
    </div>
  );
}

export const AudienceSegementView = React.memo(
  ({ storyboard, data, handleUpdateValue }) => {
    const dispatch = useDispatch();

    const { updatedSegment, segmentOrder } = data;
    const [deleteModal, setDeleteModal] = useState(false);

    const selectedCampaign = useSelector((state) => _.get(state, "personalization.campaigns.selectedCampaign", null));

    const segmentItems = useSelector((state) => _.get(state, `personalization.segments.data.${selectedCampaign}.items`)) || [];

    const selectedSegment = useSelector((state) => 
      _.get(state, "personalization.segments.selectedSegment", null)
    );

    const [segmentList, setSegmentList] = useState([]);

    useEffect(() => {
      const listIsEmpty = _.isEmpty(segmentList);
      const sizesDiffer = _.size(segmentList) !== _.size(segmentItems);
      const orderIsEmpty = _.isEmpty(segmentOrder);
    
      const cleanSegments = segments =>
        segments.map(item => _.omit(item, ['id', 'createdDate', 'modifiedDate']));
      
      const contentChanged = !_.isEqual(
        cleanSegments(segmentList),
        cleanSegments(segmentItems)
      );

      if ((listIsEmpty && !_.isEmpty(segmentItems)) || sizesDiffer || (contentChanged && orderIsEmpty)) {
        setSegmentList(segmentItems);
      }
    }, [segmentItems, segmentOrder]);

    const loading = useSelector((state) => _.get(state, "personalization.segments.loading"));

    const pagination = useSelector((state) => _.get(state, `personalization.segments.data.${selectedCampaign}.pagination`,{}) );

    const deleting =
      useSelector((state) =>
        _.get(state, "personalization.segments.deleting")
      ) || false;

    const hasMoreItems =
      _.get(pagination, "currentPage") < _.get(pagination, "totalPages") - 1;

    const updating =
      useSelector((state) =>
        _.get(state, "personalization.segments.updating")
      ) || false;

    const error =
      useSelector((state) => _.get(state, "personalization.segments.error")) ||
      null;

    const creating =
      useSelector((state) =>
        _.get(state, "personalization.segments.creating")
      ) || false;

    const handleDragEnd = (event) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = segmentList.findIndex(
          (item) => `draggable-${item.id}` === active.id
        );
        const newIndex = segmentList.findIndex(
          (item) => `draggable-${item.id}` === over.id
        );

        const orderedItems = arrayMove(segmentList, oldIndex, newIndex).map(
          (item, index) => ({ ...item, order: index })
        );

        setSegmentList(orderedItems);

        const newItems = orderedItems.map((item) =>
          _.pick(item, ["id", "order"])
        );

        handleUpdateValue("segmentOrder", newItems);
      }
    };

    const showDeleteModal = (value) => {
      if (selectedCampaign) {
        setDeleteModal(value);
      }
    };

    useEffect(() => {
      if (!selectedSegment && segmentList && segmentList.length > 0) {
        initiateSelection();
      }
    }, [segmentList, selectedSegment]);

    const initiateSelection = () => {
      if (selectedSegment) {
        return;
      }

      if (segmentList && segmentList.length > 0) {
        dispatch(selectAudienceSegment(segmentList[0].id));
      }
    };

    useEffect(() => {
      if (!selectedCampaign) {
        return;
      }
        actionGroup.start()
        dispatch(invalidateSegmnetSelection());
        handleFetchSegments(0);
        actionGroup.end();

    }, [selectedCampaign]);

    const loadMore = () => {
      const nextPage = pagination?.currentPage + 1;
      if (nextPage >= pagination?.totalPages) {
        return;
      }
      handleFetchSegments(nextPage);
    };

    const handleClone = () => {

      if (!selectedSegment) {
        return;
      }

      dispatch(cloneAudienceSegment(selectedSegment, (type)=>{
        switch (type) {
          case "success":
            dispatch(invalidateSegmnetSelection());
            toast(
              <SuccessToast
                heading={`Success`}
                description="Segment cloned successfully"
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
              }
            );
            break;
          case "error":
            toast(
              <FailureToast
                heading={`Failure`}
                description="Segment clone Failed"
              />,
              {
                type: toast.TYPE.DEFAULT,
                position: toast.POSITION.BOTTOM_RIGHT,
                autoClose: 3000,
              }
            );
            break;
        }
      }))
    }

    const handleDelete = () => {
      if (!selectedSegment) {
        return;
      }

      const request = {
        segment: selectedSegment,
        campaign: selectedCampaign,
      };

      dispatch(
        deleteAudienceSegment(request, (type) => {
          setDeleteModal(false);

          switch (type) {
            case "success":
              dispatch(invalidateSegmnetSelection());
              toast(
                <SuccessToast
                  heading={`Success`}
                  description="segment deleted successfully"
                />,
                {
                  type: toast.TYPE.DEFAULT,
                  position: toast.POSITION.BOTTOM_RIGHT,
                  autoClose: 3000,
                }
              );
              break;
            case "error":
              toast(
                <FailureToast
                  heading={`Failure`}
                  description="failed to delete segment"
                />,
                {
                  type: toast.TYPE.DEFAULT,
                  position: toast.POSITION.BOTTOM_RIGHT,
                  autoClose: 3000,
                }
              );
              break;
          }
        })
      );
    };

    const handleFetchSegments = (page) => {
      if (!storyboard) {
        return;
      }
      const request = {
        campaign: selectedCampaign,
        storyboard: storyboard,
      };

      dispatch(fetchAudienceSegments(request, page, 10));
    };

    const handleCreateSegment = () => {
      const request = {
        name: "New Segment",
        commands: {},
        order: segmentList.length < 1 ? 0 : segmentList.length,
        campaign: selectedCampaign,
        mappings: [],
        storyboard: storyboard,
      };

      dispatch(createAudienceSegment(request));
    };

    return (
      <div className={styles.audienceSegementView}>
        <div className={styles.header}>
          <span className={styles.title}>Audience Segement</span>
          <Button
            onClick={handleCreateSegment}
            disabled={creating || segmentList.length >  9}
            variant="primary"
            className={styles.segementButton}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
            Audience Segment
          </Button>
        </div>
        {loading && segmentItems && segmentItems.length === 0 && (
          <div className={cx(styles.loader, styles.modalLoader)}>
            <LoadingMessage className={styles.loadingMessage} />
          </div>
        )}
        {!loading && segmentItems && segmentItems.length === 0 && (
          <div className={styles.personalizationRow}>
            <SegmentsEmptyMessage
              campaignId={selectedCampaign}
              storyboard={storyboard}
            />
          </div>
        )}
        {segmentItems && segmentItems.length > 0 && (
          <DndContext
            autoScroll={{ threshold: { x: 10, y: 0 }, acceleration: 2 }}
            modifiers={[restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={segmentList.map((Segement) => `draggable-${Segement.id}`)}
            >
              <div
                className={styles.audienceSegmentContainer}
              >
                <Accordion
                  className={styles.accordWrapper}
                  activeKey={selectedSegment}
                >
                  {_.map(segmentList, (segmentData, index) => {
                    return (
                      <AudienceSegmentCard
                        id={`draggable-${segmentData.id}`}
                        activeSegment={selectedSegment}
                        segmentList={segmentList}
                        updatedValue={updatedSegment}
                        handleDelete={showDeleteModal}
                        handleClone={handleClone}
                        handleUpdateValue={handleUpdateValue}
                        key={segmentData.id}
                        eventKey={segmentData.id}
                        segment={segmentData}
                        index={index}
                      />
                    );
                  })}
                </Accordion>
              </div>
            </SortableContext>
          </DndContext>
        )}
        <footer className={styles.userManagementFooter}></footer>
        <DeletePersonalizationModal
          deleting={deleting}
          deleteModal={deleteModal}
          showDeleteModal={showDeleteModal}
          onDelete={handleDelete}
        />
      </div>
    );
  }
);

const AudienceSegmentCard = React.memo(
  ({
    id,
    updatedValue,
    activeSegment,
    segmentList,
    handleUpdateValue,
    handleClone,
    handleDelete,
    eventKey,
    segment,
  }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id });

    const inputRef = React.useRef(null);

    const [state, setState] = useState({
      name: "",
      rename: false,
      errorMessages: null,
    });

    const style = {
      transform: transform
        ? CSS.Transform.toString({ ...transform, scaleX: 1, scaleY: 1 })
        : undefined,
      transition,
      zIndex: isDragging ? 9999 : "auto",
      position: isDragging ? "relative" : "static",
    };

    const handleSaveChanges = () => {
      if (!state.name || state.name.trim() === "") {
        setState((prev) => ({
          ...prev,
          errorMessage: "Please provide a valid name",
        }));
        return;
      }

      handleUpdateValue("name", state.name);
      setState((prev) => ({
        ...prev,
        rename: false,
        errorMessage: null,
      }));
    };

    const handleResetValues = () => {
      if (segment.name !== state.name || activeSegment?.id !== segment.id) {
        setState((prev) => {
          return {
            ...prev,
            rename: false,
            errorMessage: null,
            name: segment.name || "",
          };
        });
        return;
      }
    };

    useEffect(() => {
      if (_.isEmpty(updatedValue.name)) {
        handleResetValues();
      }
    }, [updatedValue?.name]);

    useEffect(() => {
      if(updatedValue && updatedValue.name){
        return;
      }

      setState((prev) => {
        return {
          ...prev,
          name: segment.name || "",
        };
      });
    }, [segment]);

    useEffect(() => {
      if (state.rename) {
        inputRef.current.focus();
      }
    }, [state.rename]);

    const popover = (
      <Popover
        key={id}
        bsPrefix="popover"
        className={styles.campaignSettingsPopover}
      >
        <Popover.Body className={styles.campaignSettingsPopoverBody}>
          <div className={styles.campaignSettingsPopoverContent}>
            <span
              onClick={() => {
                setState((prev) => {
                  return {
                    ...prev,
                    rename: true,
                  };
                });
              }}
            >
              Rename
            </span>
            { segmentList.length <  10 &&
              <span
              onClick={() => {
                handleClone();
              }}
            >
              Clone
            </span>
            }
            <span
              onClick={() => {
                handleDelete(true);
              }}
            >
              Delete
            </span>
          </div>
        </Popover.Body>
      </Popover>
    );

    return (
      <div
        key={id}
        style={style}
        className={styles.segmentItem}
        ref={setNodeRef}
        {...attributes}
      >
        <div className={styles.content}>
          <i
            className={cx(styles.draggable, "cleverstory-drag")}
            {...listeners}
          />
          <div className={styles.wrapperContainer}>
            <div
              className={cx(styles.header, {
                [styles.expandHeader]: state.active,
              })}
            >
              <CustomToggle
                currentSegment={activeSegment}
                segment={segment}
                eventKey={eventKey}
              />
              <div className={styles.collapsable}>
                {state.rename && (
                  <Form.Group className={styles.siteNameFormGroup}>
                    <Form.Control
                      type="text"
                      ref={inputRef}
                      value={state.name}
                      onChange={(e) => {
                        setState((prev) => {
                          return {
                            ...prev,
                            name: e.target.value,
                            errorMessage: null,
                          };
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSaveChanges();
                        }
                      }}
                      onBlur={() => {
                        handleSaveChanges();
                      }}
                      isInvalid={state.errorMessage}
                    />
                    <Form.Control.Feedback
                      type="invalid"
                      className={styles.error}
                    >
                      {state.errorMessage}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
                {!state.rename && (
                  <TruncatedTextWithTooltip
                    className={styles.title}
                    content={state.name}
                    maxWidth={140}
                    placement="top"
                  />
                )}
                {activeSegment !== eventKey && (
                  <ConditionDescription
                    commands={_.get(segment, "commands", null)}
                  />
                )}
              </div>
            </div>
            <Accordion.Collapse
              appear={activeSegment === eventKey}
              className={styles.accordCollapse}
              eventKey={eventKey}
              style={{ display: isDragging ? "none" : "" }}
            >
              <FilterRenderer
                inline={true}
                segmentData={segment}
                handleUpdateValue={handleUpdateValue}
                updatedValue={updatedValue}
              />
            </Accordion.Collapse>
          </div>
          {activeSegment == eventKey && (
            <OverlayTrigger
              rootClose={true}
              rootCloseEvent="mousedown"
              key={id}
              trigger="click"
              placement="bottom"
              overlay={popover}
            >
              <i className={cx("cleverstory-more", styles.iconMore)} />
            </OverlayTrigger>
          )}
        </div>
      </div>
    );
  }
);

const ConditionDescription = React.memo(({ commands }) => {
  const isList = commands.is || [];
  const notList = commands.not || [];

  let description = "No conditions";

  if (isList.length > 0 || notList.length > 0) {
    const allResponses = [...isList, ...notList]
      .map((item) => item.response)
      .join('<span class="separator"> &nbsp;|&nbsp; </span>');

    description = allResponses;
  }

  return (
    <div className={cx(styles.multipleAssetLayer)}>
      <TruncatedTextWithTooltip
        className={styles.tooltip}
        content={
          <div
            dangerouslySetInnerHTML={{ __html: description }}
            className={styles.filterContent}
          />
        }
        maxWidth={340}
        placement="top"
      />
    </div>
  );
});

const FilterRenderer = ({ inline, segmentData, handleUpdateValue, updatedValue }) => {
  const dispatch = useDispatch();
  const workspaceId = useSelector(
    (state) => _.get(state, "workspaces.workspace.item.id") || null
  );
  const storyboardId = useSelector(
    (state) => _.get(state, "storyboards.storyboard.item.id") || null
  );

  const customFields = useSelector(
    (state) => _.get(state, "formFields.items") || []
  );

  const filterOptions = React.useMemo(() => {
    let reducerItems = [];

    if (Array.isArray(customFields) && customFields.length > 0) {
      reducerItems = customFields.map((item) => ({
        value: {
          type: item.userDefined ? "CUSTOM_FIELDS" : "LEAD",
          id: item.id,
        },
        label: item.name,
        data_type: "tags",
        config: {
          multiple: true,
        },
      }));
    }

    return [...segmentFilterOptions, ...reducerItems];
  }, [customFields, segmentFilterOptions]);

  const [filters, setFilters] = useState([]);

  useEffect(() => {
    if (filters && filters.length === 0) {
      initiateLayout();
    }

    if (customFields && customFields.length <= 0) {
      dispatch(fetchCustomFormFields(workspaceId));
    }
  }, []);

  useEffect(() => {
    if (updatedValue && _.isEmpty(updatedValue.commands)) {
      initiateLayout();
    }
  }, [updatedValue]);

  const initiateLayout = () => {
    const commands = segmentData?.commands || {};
    const isArray = Array.isArray(commands.is) ? commands.is : null;
    const notArray = Array.isArray(commands.not) ? commands.not : null;

    let requestFilters = [];

    if (isArray) {
      requestFilters.push(
        ...isArray.map((item) => ({
          id: guid(),
          property: { type: item.key?.toUpperCase() || "" },
          operator: item.operator,
          value: item.values,
        }))
      );
    }

    if (notArray) {
      requestFilters.push(
        ...notArray.map((item) => ({
          id: guid(),
          property: { type: item.key?.toUpperCase() || "" },
          operator: item.operator,
          value: item.values,
        }))
      );
    }

    if (requestFilters.length === 0) {
      requestFilters.push({
        id: guid(),
        property: { type: "CONTINENT" },
        operator: "IS",
      });
    }

    setFilters(requestFilters);
  };

  const transformConditions = (data = []) => {
    return data.reduce(
      (result, item) => {
        const group = item.operator === "IS" ? "is" : "not";

        result[group].push({
          key: item.property.type.toLowerCase(),
          operator: item.operator,
          values: item.value,
        });

        return result;
      },
      { is: [], not: [] }
    );
  };

  const updateFilterChanges = (filterRequests) => {
    setFilters(filterRequests);

    const commands = transformConditions(filterRequests);
    handleUpdateValue("commands", commands);
  };

  const insertFilter = () => {
    var request = {
      id: guid(),
      property: {
        type: "COUNTRY",
      },
      operator: "IS",
    };

    var requestFilters = [...filters, request];

    updateFilterChanges(requestFilters);
  };

  const deleteFilter = (index) => {
    var updated = [...filters];
    updated.splice(index, 1);

    updateFilterChanges(updated);
  };

  const onFilterChange = (filter) => {
    var updated = filters.map((item, index) => {
      if (item.id === filter.id) {
        return filter;
      }

      return item;
    });

    updateFilterChanges(updated);
  };

  return (
    <Container fluid className={styles.contentFilterContainer}>
      <SegmentContext.Provider value={segmentFilterOptions}>
        {filters.map((data, index) => {
          return (
            <SegmentFilter
              key={index}
              index={index}
              value={data}
              onChange={onFilterChange}
              onInsert={insertFilter}
              onDelete={deleteFilter}
              canDelete={index !== 0}
              showAddFilter={index === filters.length - 1}
              inline={inline}
              filters={filters}
            />
          );
        })}
      </SegmentContext.Provider>
    </Container>
  );
};

export const SegmentsEmptyMessage = ({ storyboard, campaignId }) => {
  const dispatch = useDispatch();

  const creating =
    useSelector((state) => _.get(state, "personalization.segments.creating")) ||
    false;

  const handleCreateSegments = () => {
    const request = {
      name: "New Segment",
      commands: {},
      campaign: campaignId,
      mappings: [],
      storyboard: storyboard,
    };

    dispatch(createAudienceSegment(request));
  };

  return (
    <div className={styles.emptyMediaContainer}>
      <div className={styles.emptyMessage}>
        <img
          className={cx(styles.emptyMediaImage)}
          src=""
          alt="emptyMedia_image"
        />
        <div className={styles.errorMessageContent}>
          <Button
            disabled={creating}
            variant="primary"
            className={styles.createButton}
            onClick={handleCreateSegments}
          >
            <i className={cx(styles.icon, "cleverstory-plus")} />
            Audience Segment
          </Button>
          <div className={styles.errorMessageTitle}>
            <h2 className={styles.heading}>Weather forecast</h2>
          </div>
          <div className={styles.errorMessageSubText}>
            <span>
              Clear skies and <br /> empty screens
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};